import React, { useState, type JSX } from "react";
import { EllipsisHorizontalIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import ChatContextMenu from "../ChatContextMenu_Component";
import { newTabAwareClick } from "./NewChatLink";

/** "Chats" section heading with the search-chats affordance. */
export function ChatsHeading({ onOpenSearch }: { onOpenSearch: () => void }): JSX.Element {
  return (
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-sm font-cvcBody font-normal text-gray-500">Chats</h3>
      <button
        onClick={onOpenSearch}
        className="p-1 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
        aria-label="Search chats"
      >
        <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
      </button>
    </div>
  );
}

export interface SidebarProject {
  id: string;
  name?: string;
}

export interface ChatTarget {
  id: string;
  name: string;
}

interface MenuAnchor {
  top: number;
  left: number;
}

interface ChatListItemProps {
  project: SidebarProject;
  isActive: boolean;
  isMenuOpen: boolean;
  menuPosition: MenuAnchor;
  onSelect?: (projectId: string) => void;
  onToggleMenu: (projectId: string, anchor: MenuAnchor) => void;
  onCloseMenu: () => void;
  onRename: (target: ChatTarget) => void;
  onDelete: (target: ChatTarget) => void;
}

function ChatListItem({
  project,
  isActive,
  isMenuOpen,
  menuPosition,
  onSelect,
  onToggleMenu,
  onCloseMenu,
  onRename,
  onDelete,
}: ChatListItemProps): JSX.Element {
  const displayName = project.name || `Project ${project.id.slice(0, 8)}`;

  return (
    <div className="group relative">
      <a
        href={`${window.location.origin}?projectId=${project.id}`}
        onClick={(e) => newTabAwareClick(e, () => onSelect && onSelect(project.id))}
        className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors duration-200 touch-manipulation ${
          isActive ? "bg-blue-50 font-medium" : "hover:bg-gray-100"
        }`}
        style={{ color: "#234E81" }}
      >
        <span className="truncate flex-1" title={displayName}>
          {displayName}
        </span>
      </a>

      {/* Ellipsis menu button - appears on hover */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          const rect = e.currentTarget.getBoundingClientRect();
          onToggleMenu(project.id, { top: rect.bottom + 4, left: rect.left });
        }}
        className={`absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-200 focus:outline-none transition-opacity duration-150 ${
          isMenuOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto focus-visible:opacity-100 focus-visible:pointer-events-auto"
        }`}
        aria-label="Chat options"
      >
        <EllipsisHorizontalIcon className="h-4 w-4" style={{ color: "#234E81" }} />
      </button>

      {isMenuOpen && (
        <ChatContextMenu
          isOpen={true}
          onClose={onCloseMenu}
          position={menuPosition}
          onRename={() => {
            onCloseMenu();
            onRename({ id: project.id, name: project.name ?? "" });
          }}
          onDelete={() => {
            onCloseMenu();
            onDelete({ id: project.id, name: displayName });
          }}
        />
      )}
    </div>
  );
}

interface ChatListProps {
  projects: SidebarProject[];
  projectsLoading: boolean;
  currentProjectId: string | null;
  onProjectClick?: (projectId: string) => void;
  onRename: (target: ChatTarget) => void;
  onDelete: (target: ChatTarget) => void;
}

/** The chat history list, including each row's context menu. */
export default function ChatList({
  projects,
  projectsLoading,
  currentProjectId,
  onProjectClick,
  onRename,
  onDelete,
}: ChatListProps): JSX.Element {
  const [openMenuProjectId, setOpenMenuProjectId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<MenuAnchor>({ top: 0, left: 0 });

  const closeMenu = (): void => setOpenMenuProjectId(null);

  return (
    <div className="mt-4">
      {projectsLoading && <p className="text-xs text-gray-400">Loading chats...</p>}

      {!projectsLoading && projects.length === 0 && (
        <p className="text-xs text-gray-400">Your chats will appear here.</p>
      )}

      {!projectsLoading && projects.length > 0 && (
        <nav className="space-y-1">
          {projects.map((project) => (
            <ChatListItem
              key={project.id}
              project={project}
              isActive={project.id === currentProjectId}
              isMenuOpen={openMenuProjectId === project.id}
              menuPosition={menuPosition}
              onSelect={onProjectClick}
              onToggleMenu={(projectId, anchor) => {
                setMenuPosition(anchor);
                setOpenMenuProjectId(openMenuProjectId === projectId ? null : projectId);
              }}
              onCloseMenu={closeMenu}
              onRename={onRename}
              onDelete={onDelete}
            />
          ))}
        </nav>
      )}
    </div>
  );
}
